import type { AutomationLane, Pattern } from '@busswerks/shared'
import { TICKS_PER_STEP } from '@busswerks/shared'

/**
 * Automation resolver.
 *
 * Walks a pattern's automation lanes at a given tick and returns
 * the interpolated values keyed by `paramPath`. Callers scope to
 * the paths they care about (e.g. the trigger path applies
 * `pad/<idx>/preset/<name>` overrides to a pad's preset before
 * firing).
 *
 * Scope (Wave 5c): only per-pad preset overrides land in the live
 * fire path. Bus effect param automation + project-level (`project/bpm`)
 * ride on top of the same resolver but aren't threaded into the
 * engine yet — follow-up PRs hook them into `Bus.applyKindParams`
 * and the sequencer clock respectively.
 *
 * Interpolation: linear between adjacent breakpoints, held past
 * the last point, echoed from the first point before its tick.
 * Lanes with `enabled === false` are skipped entirely.
 */

export interface ResolvedAutomation {
  /** Flat lookup by `paramPath` — `{ 'pad/0/preset/cutoff': 800, ... }`. */
  values: Record<string, number>
}

/** Convert a step index (0..stepCount-1) into pattern ticks. */
export function stepToTick(stepIndex: number): number {
  return stepIndex * TICKS_PER_STEP
}

/** Sample every enabled lane at `tick`. Pure — no side effects. */
export function resolveAutomationAtTick(
  pattern: Pick<Pattern, 'automation'>,
  tick: number,
): ResolvedAutomation {
  const values: Record<string, number> = {}
  const lanes = pattern.automation ?? []
  for (const lane of lanes) {
    if (lane.enabled === false) continue
    const v = sampleLane(lane, tick)
    if (v !== undefined) values[lane.paramPath] = v
  }
  return { values }
}

/** Linear-interpolate one lane at `tick`. Returns `undefined`
 *  when the lane has no breakpoints (nothing to sample). */
export function sampleLane(
  lane: AutomationLane,
  tick: number,
): number | undefined {
  const points = lane.points
  if (points.length === 0) return undefined
  if (points.length === 1) return points[0].value
  // Points are conventionally sorted but we don't trust the
  // caller. Scan + clamp.
  let prev = points[0]
  let next = points[points.length - 1]
  if (tick <= prev.tick) return prev.value
  if (tick >= next.tick) return next.value
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    if (tick >= a.tick && tick <= b.tick) {
      prev = a
      next = b
      break
    }
  }
  const span = next.tick - prev.tick
  if (span <= 0) return prev.value
  const t = (tick - prev.tick) / span
  return prev.value + (next.value - prev.value) * t
}

/**
 * Extract per-pad preset overrides from an automation snapshot.
 * Lanes with `paramPath === 'pad/<idx>/preset/<name>'` fold into
 * `{ padIndex: { name: value } }`; anything else is dropped.
 *
 * Used by the trigger path when firing instrument pads: the
 * returned overrides merge on top of `pad.preset` so automation
 * values win over the static preset without mutating the pad
 * definition.
 */
export function extractPadPresetOverrides(
  snapshot: ResolvedAutomation,
): Record<number, Record<string, number>> {
  const out: Record<number, Record<string, number>> = {}
  for (const [path, value] of Object.entries(snapshot.values)) {
    const m = path.match(/^pad\/(\d+)\/preset\/(.+)$/)
    if (!m) continue
    const padIndex = Number(m[1])
    const paramName = m[2]
    const bucket = out[padIndex] ?? (out[padIndex] = {})
    bucket[paramName] = value
  }
  return out
}
